<template>
	<Teleport to="#navs">
		<Transition
			enter-from-class="translate-x-[max(theme(spacing.480),_var(--2fr-container-width))] opacity-0 blur-[5px]"
			leave-to-class="translate-x-[max(theme(spacing.480),_var(--2fr-container-width))] opacity-0 blur-[5px]"
			enter-active-class="transition-all duration-500 ease-in-out"
			leave-active-class="transition-all duration-500 ease-in-out"
		>
			<div ref="drawer" id="nav-drawer" class="bg-gray-900 absolute z-10 -inset-y-10 right-0 w-[min(theme(spacing.480),_100vw)] lg:w-[calc(var(--2fr-container-width)_+_var(--container-margin))] lg:min-w-480 pr-[var(--container-margin)] py-10 focus-visible:outline-none after:absolute after:inset-0 after:pointer-events-none after:outline-0 focus-visible:after:outline-2 after:outline-dashed after:outline-white/15 after:-outline-offset-4" v-cloak v-if="drawerOpen" v-click-outside="closeDrawer" @click.capture="handleClick" tabindex="-1">
				<slot :triggered-by-floaty-nav="triggeredByFloatyNav"></slot>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { lock, unlock } from "@js/body-scroll-lock.js";
import { create, activate, deactivate } from "@js/focus-trap.js";
import { scrollToHash } from "@js/jump-links";
import { store, mutations } from "@js/vue/store.js";

const drawer = ref(null);
const focusTrap = ref(null);
const drawerOpen = computed(() => store.navDrawerOpen);

function closeDrawer() {
	mutations.closeNavDrawer();
}

watch(drawerOpen, async (newValue) => {
	if (newValue) {
		lock();
		await nextTick();
		focusTrap.value = create(drawer.value);
		activate(focusTrap.value);
	} else {
		unlock();
		if (focusTrap.value) {
			deactivate(focusTrap.value);
		}
	}
}, { immediate: true });

function handleKeydown(e) {
	if (e.key === 'Escape' && drawerOpen.value) {
		closeDrawer();
	}
}

const handleClick = async (e) => {
	// Check if this is an artificial click event
	if (e.isTrusted === false) {
		return // Skip handling programmatic clicks
	}

	// Stop the original click event
	e.stopPropagation()
	e.preventDefault()

	// Check if the click event is on a link or button
	const isClickable = e.target.closest('a, button');
	// Check if the link is an anchor link
	const isAnchorLink = isClickable && isClickable.tagName === 'A' && isClickable.getAttribute('href').startsWith('#');

	// If the link is an anchor link, close the drawer
	if (isAnchorLink) {
		// Wait for drawer to partly close before proceeding
		closeDrawer();
		await new Promise(resolve => setTimeout(resolve, 300));

		scrollToHash(isClickable.getAttribute('href'), e);
	}

	// If the link is not an anchor link, simulate a click event on it
	if (isClickable && !isAnchorLink) {
		isClickable.click()
	}

}

// Header height tracking (same as FloatyNav)
let headerHeight = 0;

/**
 * Get the height of the main header element
 * This determines if FloatyNav could be active
 */
const getHeaderHeight = () => {
	const header = document.querySelector('#hero');
	if (header) {
		headerHeight = header.offsetHeight;
	}
};

/**
 * Check if the current scroll position indicates FloatyNav could be active
 * FloatyNav only becomes active when header is completely off-screen
 */
const triggeredByFloatyNav = computed(() => {
	if (typeof window === 'undefined') return false;

	const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
	return currentScroll >= headerHeight;
});

/**
 * Handle window resize - recalculate header height
 * Header height may change on mobile/tablet orientation changes
 */
const handleResize = () => {
	getHeaderHeight();
};

onMounted(() => {
	// Initialize header height measurement
	getHeaderHeight();

	// Set up keydown listener for Escape key
	window.addEventListener('keydown', handleKeydown, { passive: true });

	// Set up resize listener to recalculate header height
	window.addEventListener('resize', handleResize, { passive: true });
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleKeydown);
	window.removeEventListener('resize', handleResize);
});
</script>
